import type { DevisFrise, FactureFrise } from '@imp/shared/frise-chantier';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { listColonnesSuivi } from '@/modules/chantiers/suivi-api';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export const CHANTIER_STATUT_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  planifie: 'Planifié',
  en_cours: 'En cours',
  pause: 'En pause',
  termine: 'Terminé',
  receptionne: 'Réceptionné',
  sav: 'SAV',
  archive: 'Archivé',
  annule: 'Annulé',
};

export interface Employe {
  id: string;
  nom: string;
  prenom: string;
  poste: string | null;
  couleur_agenda: string | null;
}

export interface ChantierListItem {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  type_travaux: string | null;
  ville: string | null;
  avancement_pourcentage: number;
  contact_id: string | null;
}

export type PrepStatut = 'na' | 'todo' | 'done';
export type PrepKey = 'vt' | 'occupation_sol' | 'fourniture' | 'acompte' | 'dp_mairie';

// Étapes de préparation d'origine. Elles ne servent PLUS de référence : les
// colonnes vivent en base et se configurent à l'écran Suivi (voir suivi-api.ts,
// COLONNES_DEFAUT). Gardé pour les écrans qui n'ont pas encore basculé.
export const PREP_ITEMS: { key: PrepKey; label: string; alerte: string }[] = [
  { key: 'vt', label: 'Visite technique', alerte: 'Visite technique à réaliser' },
  {
    key: 'occupation_sol',
    label: "Demande d'occupation du sol",
    alerte: "Demande d'occupation du sol à faire",
  },
  {
    key: 'fourniture',
    label: 'Commande de fournitures',
    alerte: 'Commande de fournitures à passer',
  },
  { key: 'acompte', label: 'Acompte', alerte: 'Acompte à demander / encaisser' },
  {
    key: 'dp_mairie',
    label: 'Déclaration préalable en mairie',
    alerte: 'Déclaration préalable en mairie à déposer',
  },
];

export const PREP_STATUT_LABELS: Record<PrepStatut, string> = {
  na: 'Non concerné',
  todo: 'À faire',
  done: 'Fait',
};

export interface ChantierMeta {
  telephone?: string;
  montant_achats?: number;
  montant_vente_ht?: number;
  jours_realises?: number;
  commercial_id?: string;
  nacelle_requise?: boolean;
  nacelle_magasin?: string;
  nacelle_commandee?: boolean;
  /**
   * Étapes de préparation cochées, par clé de colonne. Les colonnes sont
   * configurables depuis l'écran Suivi (migration 19) : la clé est donc libre,
   * pas cantonnée aux cinq étapes d'origine.
   */
  prep?: Partial<Record<string, PrepStatut>>;
}

export interface Chantier {
  id: string;
  numero: string;
  libelle: string;
  description: string | null;
  contact_id: string | null;
  type_travaux: string | null;
  statut: string;
  chef_chantier_id: string | null;
  adresse_ligne1: string | null;
  adresse_ligne2: string | null;
  code_postal: string | null;
  ville: string | null;
  latitude: string | null;
  longitude: string | null;
  date_prevue_debut: string | null;
  date_prevue_fin: string | null;
  avancement_pourcentage: number;
  notes: string | null;
  metadata: ChantierMeta | null;
}

export interface ChantierInput {
  libelle: string;
  description: string | null;
  contact_id: string | null;
  type_travaux: string | null;
  statut: string;
  chef_chantier_id: string | null;
  adresse_ligne1: string | null;
  adresse_ligne2: string | null;
  code_postal: string | null;
  ville: string | null;
  latitude: string | null;
  longitude: string | null;
  date_prevue_debut: string | null;
  date_prevue_fin: string | null;
  avancement_pourcentage: number;
  notes: string | null;
  metadata: ChantierMeta;
}

export interface ChantierEvenement {
  id: string;
  titre: string;
  debut_at: string;
  fin_at: string;
  statut: string;
  type_id: string | null;
}

export async function listEmployes(): Promise<Employe[]> {
  const { data, error } = await supabase
    .from('employes')
    .select('id,nom,prenom,poste,couleur_agenda')
    .eq('actif', true)
    .order('nom');
  if (error) throw new Error(error.message);
  return data ?? [];
}

export function employeNom(e: Employe): string {
  return `${e.prenom} ${e.nom}`.trim();
}

export async function listChantiers(): Promise<ChantierListItem[]> {
  const { data, error } = await supabase
    .from('chantiers')
    .select('id,numero,libelle,statut,type_travaux,ville,avancement_pourcentage,contact_id')
    .order('numero', { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getChantier(id: string): Promise<Chantier> {
  const { data, error } = await supabase
    .from('chantiers')
    .select(
      'id,numero,libelle,description,contact_id,type_travaux,statut,chef_chantier_id,adresse_ligne1,adresse_ligne2,code_postal,ville,latitude,longitude,date_prevue_debut,date_prevue_fin,avancement_pourcentage,notes,metadata',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function createChantier(input: ChantierInput): Promise<string> {
  const { data: numero, error: numErr } = await supabase.rpc('next_document_number', {
    p_type_document: 'chantier',
  });
  if (numErr) throw new Error(numErr.message);
  const { data, error } = await supabase
    .from('chantiers')
    .insert({ ...input, numero: numero as unknown as string, organization_id: ORG_ID })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function updateChantier(id: string, input: ChantierInput): Promise<void> {
  const { error } = await supabase.from('chantiers').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

// Suppression d'un chantier (réservée aux admins côté UI). Les sous-éléments
// (photos, pointages, métrés, documents, garanties) sont supprimés en cascade ;
// les devis et factures liés sont CONSERVÉS (détachés) — conformité NF525.
export async function deleteChantier(id: string): Promise<void> {
  const { error } = await supabase.from('chantiers').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface ChantierPilotage {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  contact_id: string | null;
  chef_chantier_id: string | null;
  /** Les dates de travaux rattachent un chantier non facturé à un mois. */
  date_prevue_debut: string | null;
  date_prevue_fin: string | null;
  metadata: ChantierMeta | null;
}

export async function listChantiersPilotage(): Promise<ChantierPilotage[]> {
  // Lu par tranches : PostgREST plafonne les lignes rendues SANS le signaler,
  // et un chantier manquant retirerait sa marge du mois sans la moindre erreur.
  return lireTout<ChantierPilotage>((de, a) =>
    supabase
      .from('chantiers')
      .select(
        'id,numero,libelle,statut,contact_id,chef_chantier_id,date_prevue_debut,date_prevue_fin,metadata',
      )
      .is('deleted_at', null)
      .order('id')
      .range(de, a),
  );
}

/** Les devis et factures d'un chantier, tels que la frise les lit. */
export interface HistoriqueChantier {
  devis: DevisFrise[];
  factures: FactureFrise[];
}

/**
 * Ce que le CRM sait du chantier côté commercial : ses devis et ses factures.
 * Une lecture qui échoue remonte : la frise dirait sinon « aucun devis », et
 * proposerait d'en créer un pour un chantier déjà signé.
 */
export async function getHistoriqueChantier(chantierId: string): Promise<HistoriqueChantier> {
  const [devisR, facturesR] = await Promise.all([
    supabase
      .from('devis')
      .select('id,numero,statut')
      .eq('chantier_id', chantierId)
      .is('deleted_at', null)
      .order('date_emission')
      .overrideTypes<{ id: string; numero: string | null; statut: string }[], { merge: false }>(),
    supabase
      .from('factures')
      .select('id,numero,kind,statut,montant_ttc,montant_paye')
      .eq('chantier_id', chantierId)
      .is('deleted_at', null)
      .order('created_at')
      .overrideTypes<
        {
          id: string;
          numero: string | null;
          kind: string;
          statut: string;
          montant_ttc: number | string;
          montant_paye: number | string;
        }[],
        { merge: false }
      >(),
  ]);
  if (devisR.error) throw new Error(devisR.error.message);
  if (facturesR.error) throw new Error(facturesR.error.message);
  return {
    devis: devisR.data,
    factures: facturesR.data.map((f) => ({
      id: f.id,
      numero: f.numero,
      kind: f.kind,
      statut: f.statut,
      montantTtc: Number(f.montant_ttc),
      montantPaye: Number(f.montant_paye),
    })),
  };
}

export async function listEvenementsForChantier(chantierId: string): Promise<ChantierEvenement[]> {
  const { data, error } = await supabase
    .from('evenements')
    .select('id,titre,debut_at,fin_at,statut,type_id')
    .eq('chantier_id', chantierId)
    .order('debut_at');
  if (error) throw new Error(error.message);
  return data ?? [];
}

// Lien de navigation GPS (ouvre l'app de cartographie du téléphone).
export function itineraireUrl(
  c: Pick<Chantier, 'latitude' | 'longitude' | 'adresse_ligne1' | 'code_postal' | 'ville'>,
): string | null {
  if (c.latitude && c.longitude) {
    return `https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}`;
  }
  const addr = [c.adresse_ligne1, c.code_postal, c.ville].filter(Boolean).join(' ');
  if (addr.trim() !== '') {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
  }
  return null;
}

// ----- Photos (Supabase Storage, bucket privé 'chantiers') ------------------

export const PHOTO_PHASE_LABELS: Record<string, string> = {
  avant: 'Avant',
  pendant: 'Pendant',
  apres: 'Après',
  sav: 'SAV',
};

export interface ChantierPhoto {
  id: string;
  storage_key: string;
  phase: string;
  legende: string | null;
  url: string | null;
}

export async function listPhotos(chantierId: string): Promise<ChantierPhoto[]> {
  const { data, error } = await supabase
    .from('chantier_photos')
    .select('id,storage_key,phase,legende')
    .eq('chantier_id', chantierId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as {
    id: string;
    storage_key: string;
    phase: string;
    legende: string | null;
  }[];
  return Promise.all(
    rows.map(async (r) => {
      const { data: signed } = await supabase.storage
        .from('chantiers')
        .createSignedUrl(r.storage_key, 3600);
      return { ...r, url: signed?.signedUrl ?? null };
    }),
  );
}

export async function uploadPhoto(chantierId: string, file: File, phase: string): Promise<void> {
  const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
  const key = `${ORG_ID}/${chantierId}/${crypto.randomUUID()}.${ext}`;
  const up = await supabase.storage.from('chantiers').upload(key, file);
  if (up.error) throw new Error(up.error.message);
  const { error } = await supabase.from('chantier_photos').insert({
    organization_id: ORG_ID,
    chantier_id: chantierId,
    storage_key: key,
    phase,
    taille_octets: file.size,
  });
  if (error) throw new Error(error.message);
}

export async function deletePhoto(photo: ChantierPhoto): Promise<void> {
  await supabase.storage.from('chantiers').remove([photo.storage_key]);
  const { error } = await supabase.from('chantier_photos').delete().eq('id', photo.id);
  if (error) throw new Error(error.message);
}

// ----- Documents PDF (Supabase Storage, bucket privé 'chantiers') ------------

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  devis: 'Devis fournisseur',
  plan: 'Plan',
  permis: 'Permis / autorisation',
  facture: 'Facture fournisseur',
  technique: 'Fiche technique',
  autre: 'Autre',
};

export interface ChantierDocument {
  id: string;
  nom: string;
  type: string;
  storage_key: string;
  taille_octets: number | null;
  url: string | null;
}

export async function listDocuments(chantierId: string): Promise<ChantierDocument[]> {
  const { data, error } = await supabase
    .from('chantier_documents')
    .select('id,nom,type,storage_key,taille_octets')
    .eq('chantier_id', chantierId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as {
    id: string;
    nom: string;
    type: string;
    storage_key: string;
    taille_octets: number | null;
  }[];
  return Promise.all(
    rows.map(async (r) => {
      const { data: signed } = await supabase.storage
        .from('chantiers')
        .createSignedUrl(r.storage_key, 3600);
      return { ...r, url: signed?.signedUrl ?? null };
    }),
  );
}

export async function uploadDocument(chantierId: string, file: File, type: string): Promise<void> {
  const ext = (file.name.split('.').pop() ?? 'pdf').toLowerCase();
  const key = `${ORG_ID}/${chantierId}/docs/${crypto.randomUUID()}.${ext}`;
  const up = await supabase.storage
    .from('chantiers')
    .upload(key, file, { contentType: file.type || 'application/pdf' });
  if (up.error) throw new Error(up.error.message);
  const { error } = await supabase.from('chantier_documents').insert({
    organization_id: ORG_ID,
    chantier_id: chantierId,
    nom: file.name,
    type,
    storage_key: key,
    mime_type: file.type || 'application/pdf',
    taille_octets: file.size,
  });
  if (error) throw new Error(error.message);
}

export async function deleteDocument(doc: ChantierDocument): Promise<void> {
  await supabase.storage.from('chantiers').remove([doc.storage_key]);
  const { error } = await supabase.from('chantier_documents').delete().eq('id', doc.id);
  if (error) throw new Error(error.message);
}

// ----- Pointage des heures (saisie sur la fiche chantier + récap RH) ---------

export interface Pointage {
  id: string;
  jour: string;
  employe_id: string;
  employe_nom: string;
  heures_normales: number;
  heures_sup_25: number;
  heures_sup_50: number;
  paniers_repas: number;
  km: number;
  notes: string | null;
}

export interface PointageInput {
  employe_id: string;
  jour: string;
  heures_normales: string;
  heures_sup_25: string;
  heures_sup_50: string;
  paniers_repas: string;
  km: string;
  notes: string;
}

type NomEmbed = { nom: string; prenom: string } | { nom: string; prenom: string }[] | null;
function embedNom(e: NomEmbed): string {
  const o = Array.isArray(e) ? e[0] : e;
  return o ? `${o.prenom} ${o.nom}`.trim() : '—';
}
const num = (v: number | string): number => (typeof v === 'number' ? v : Number(v) || 0);

interface PointageRow {
  id: string;
  jour: string;
  employe_id: string;
  heures_normales: number | string;
  heures_sup_25: number | string;
  heures_sup_50: number | string;
  paniers_repas: number;
  indemnite_trajet_km: number | string;
  notes: string | null;
  employes: NomEmbed;
}

function mapPointage(r: PointageRow): Pointage {
  return {
    id: r.id,
    jour: r.jour,
    employe_id: r.employe_id,
    employe_nom: embedNom(r.employes),
    heures_normales: num(r.heures_normales),
    heures_sup_25: num(r.heures_sup_25),
    heures_sup_50: num(r.heures_sup_50),
    paniers_repas: r.paniers_repas,
    km: num(r.indemnite_trajet_km),
    notes: r.notes,
  };
}

export async function listPointages(chantierId: string): Promise<Pointage[]> {
  const { data, error } = await supabase
    .from('chantier_pointages')
    .select(
      'id,jour,employe_id,heures_normales,heures_sup_25,heures_sup_50,paniers_repas,indemnite_trajet_km,notes,employes(nom,prenom)',
    )
    .eq('chantier_id', chantierId)
    .order('jour', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapPointage(r as unknown as PointageRow));
}

// Saisie d'un pointage. Upsert sur (chantier, jour, intervenant) : ressaisir
// le même jour pour la même personne met à jour les heures.
export async function savePointage(chantierId: string, input: PointageInput): Promise<void> {
  const { error } = await supabase.from('chantier_pointages').upsert(
    {
      organization_id: ORG_ID,
      chantier_id: chantierId,
      employe_id: input.employe_id,
      jour: input.jour,
      heures_normales: input.heures_normales === '' ? '0' : input.heures_normales.replace(',', '.'),
      heures_sup_25: input.heures_sup_25 === '' ? '0' : input.heures_sup_25.replace(',', '.'),
      heures_sup_50: input.heures_sup_50 === '' ? '0' : input.heures_sup_50.replace(',', '.'),
      paniers_repas: Number(input.paniers_repas) || 0,
      indemnite_trajet_km: input.km === '' ? '0' : input.km.replace(',', '.'),
      notes: input.notes.trim() === '' ? null : input.notes.trim(),
    },
    { onConflict: 'chantier_id,jour,employe_id' },
  );
  if (error) throw new Error(error.message);
}

export async function deletePointage(id: string): Promise<void> {
  const { error } = await supabase.from('chantier_pointages').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// RH : tous les pointages d'une période (avec intervenant + chantier).
export interface PointageRH extends Pointage {
  chantier_id: string;
  chantier_numero: string;
  chantier_libelle: string;
}

export async function listPointagesRange(start: string, end: string): Promise<PointageRH[]> {
  const { data, error } = await supabase
    .from('chantier_pointages')
    .select(
      'id,jour,employe_id,chantier_id,heures_normales,heures_sup_25,heures_sup_50,paniers_repas,indemnite_trajet_km,notes,employes(nom,prenom),chantiers(numero,libelle)',
    )
    .gte('jour', start)
    .lte('jour', end)
    .order('jour', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((raw) => {
    const r = raw as unknown as PointageRow & {
      chantier_id: string;
      chantiers: { numero: string; libelle: string } | { numero: string; libelle: string }[] | null;
    };
    const ch = Array.isArray(r.chantiers) ? r.chantiers[0] : r.chantiers;
    return {
      ...mapPointage(r),
      chantier_id: r.chantier_id,
      chantier_numero: ch?.numero ?? '—',
      chantier_libelle: ch?.libelle ?? '',
    };
  });
}

// ----- Fiche chantier imprimable --------------------------------------------
//
// Tout ce qu'il faut savoir d'un chantier sur une page, pour l'emporter sur le
// toit ou le donner à l'équipe. Rassemblé en une fonction : l'écran
// d'impression ne doit pas enchaîner huit requêtes avant de s'afficher.
//
// Chaque bloc peut manquer sans empêcher les autres — un chantier sans devis,
// sans client rattaché ou sans étape de préparation reste imprimable.

export interface LigneDocumentFiche {
  numero: string;
  date: string | null;
  /** HT pour un devis, TTC pour une facture. */
  montant: number;
  statut: string;
  /** Pour une facture : ce qui reste dû. `null` pour un devis. */
  reste: number | null;
  /** TTC du devis — ce que le client paie, et ce qu'il retient. `null` pour une facture. */
  montantTtc: number | null;
  /** Le taux de TVA, quand le devis n'en porte qu'un seul. */
  tauxTva: number | null;
  /** Le pourcentage d'acompte demandé, et ce qu'il représente. */
  acomptePourcentage: number | null;
  acompteMontant: number | null;
}

export interface EtapeFiche {
  label: string;
  statut: PrepStatut;
}

export interface FicheChantier {
  chantier: Chantier;
  client: {
    nom: string;
    telephone: string | null;
    mobile: string | null;
    email: string | null;
  } | null;
  chefChantier: string | null;
  devis: LigneDocumentFiche[];
  factures: LigneDocumentFiche[];
  preparation: EtapeFiche[];
  /** Les rendez-vous à venir, les plus proches d'abord. */
  rendezVous: { debut: string; titre: string }[];
  /** Les photos du dossier, pour la dernière section de la fiche. */
  photos: ChantierPhoto[];
}

interface ContactFiche {
  type_personne: string;
  civilite: string | null;
  nom: string | null;
  prenom: string | null;
  raison_sociale: string | null;
  email: string | null;
  telephone: string | null;
  telephone_mobile: string | null;
}

function nomDuClient(c: ContactFiche): string {
  if (c.type_personne === 'pro') return c.raison_sociale ?? '';
  return [c.civilite, c.prenom, c.nom].filter((v) => v !== null && v !== '').join(' ');
}

export async function getFicheChantier(id: string): Promise<FicheChantier> {
  const chantier = await getChantier(id);

  // Les blocs annexes sont lus en parallèle : la fiche ne doit pas enchaîner
  // six allers-retours avant de s'afficher.
  const [contactRes, chefRes, devisRes, facturesRes, colonnes, rdvRes] = await Promise.all([
    chantier.contact_id === null
      ? Promise.resolve(null)
      : supabase
          .from('contacts')
          .select(
            'type_personne,civilite,nom,prenom,raison_sociale,email,telephone,telephone_mobile',
          )
          .eq('id', chantier.contact_id)
          .maybeSingle()
          .overrideTypes<ContactFiche, { merge: false }>(),
    chantier.chef_chantier_id === null
      ? Promise.resolve(null)
      : supabase
          .from('employes')
          .select('nom,prenom')
          .eq('id', chantier.chef_chantier_id)
          .maybeSingle()
          .overrideTypes<{ nom: string; prenom: string }, { merge: false }>(),
    supabase
      .from('devis')
      .select(
        'numero,date_emission,montant_ht,montant_tva,montant_ttc,statut,acompte_pourcentage,detail_tva',
      )
      .eq('chantier_id', id)
      .is('deleted_at', null)
      .order('date_emission')
      .overrideTypes<
        {
          numero: string;
          date_emission: string | null;
          montant_ht: string;
          montant_tva: string;
          montant_ttc: string;
          statut: string;
          acompte_pourcentage: number | null;
          detail_tva: { tauxValeur: number }[] | null;
        }[],
        { merge: false }
      >(),
    supabase
      .from('factures')
      .select('numero,date_emission,montant_ttc,montant_paye,statut,kind')
      .eq('chantier_id', id)
      .is('deleted_at', null)
      .neq('statut', 'brouillon')
      .order('date_emission')
      .overrideTypes<
        {
          numero: string;
          date_emission: string | null;
          montant_ttc: string;
          montant_paye: string;
          statut: string;
          kind: string;
        }[],
        { merge: false }
      >(),
    listColonnesSuivi(),
    supabase
      .from('evenements')
      .select('debut_at,titre')
      .eq('chantier_id', id)
      .is('deleted_at', null)
      .gte('debut_at', new Date().toISOString())
      .order('debut_at')
      .limit(5)
      .overrideTypes<{ debut_at: string; titre: string }[], { merge: false }>(),
  ]);

  const c = contactRes?.data ?? null;
  const chef = chefRes?.data ?? null;

  const devis = (devisRes.data ?? []).map((d) => {
    const ht = Number(d.montant_ht);
    const ttc = Number(d.montant_ttc);
    // Le taux n'est affiché que si le devis n'en porte qu'un : sur un devis à
    // deux taux, en annoncer un seul serait faux.
    const taux = d.detail_tva ?? [];
    const tauxUnique = taux.length === 1 ? (taux[0]?.tauxValeur ?? null) : null;
    const pct = d.acompte_pourcentage;
    return {
      numero: d.numero,
      date: d.date_emission,
      montant: ht,
      statut: d.statut,
      reste: null,
      montantTtc: ttc,
      tauxTva: tauxUnique,
      acomptePourcentage: pct,
      acompteMontant: pct === null ? null : Math.round(ttc * pct) / 100,
    };
  });

  const factures = (facturesRes.data ?? []).map((f) => {
    // Un avoir se lit en négatif : il rend de l'argent, il n'en réclame pas.
    const signe = f.kind === 'avoir' ? -1 : 1;
    const ttc = Number(f.montant_ttc) * signe;
    return {
      numero: f.numero,
      date: f.date_emission,
      montant: ttc,
      statut: f.statut,
      reste: Math.round((ttc - Number(f.montant_paye) * signe) * 100) / 100,
      montantTtc: null,
      tauxTva: null,
      acomptePourcentage: null,
      acompteMontant: null,
    };
  });

  // Les étapes cochées ne vivent pas dans une table : elles sont rangées dans
  // `chantiers.metadata.prep`, par clé de colonne. Les colonnes, elles, sont
  // configurables depuis l'écran Suivi — c'est d'elles que viennent le libellé
  // et l'ordre d'affichage.
  const prep = chantier.metadata?.prep ?? {};
  const preparation = colonnes
    .filter((col) => col.actif)
    .map((col) => ({ label: col.label, statut: prep[col.cle] ?? 'na' }));

  const rendezVous = (rdvRes.data ?? []).map((e) => ({ debut: e.debut_at, titre: e.titre }));

  // Les photos demandent une URL signée par fichier : une fiche sans photo
  // reste une fiche, et un lien qui échoue ne doit pas empêcher d'imprimer
  // l'adresse et le téléphone du client.
  let photos: ChantierPhoto[] = [];
  try {
    photos = await listPhotos(id);
  } catch {
    photos = [];
  }

  return {
    chantier,
    client:
      c === null
        ? null
        : {
            nom: nomDuClient(c),
            telephone: c.telephone,
            mobile: c.telephone_mobile,
            email: c.email,
          },
    chefChantier: chef === null ? null : `${chef.prenom} ${chef.nom}`.trim(),
    devis,
    factures,
    preparation,
    rendezVous,
    photos,
  };
}
